import { HelpCircle, LayoutGrid, LogOut, Settings } from "lucide-react";
import { Link } from "react-router-dom";

import { useLogout, type Session } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * User-icon dropdown. Lives at the foot of the left rail, under the
 * theme toggle and Settings (it sat in the header until the app-frame
 * change moved the account controls into the rail).
 *
 * Consolidates the four previous top-right elements (⌘K, theme
 * toggle, email text, Sign out button) into the standard "circular
 * avatar with initial" trigger every ATTMOS suite portal exposes —
 * matches orbit's TopBar so a user moving between apps sees the
 * same identity affordance in the same spot.
 *
 * Menu shape mirrors orbit:
 *
 *   ┌────────────────────────────┐
 *   │ Alejandro Blanco           │  ← name (or email fallback)
 *   │ ablanco@attmosdiscovery.com│  ← email (or "" when name IS the email)
 *   ├────────────────────────────┤
 *   │  ⚙  Settings               │
 *   │  ▦  Apps                    │
 *   │  ?  Help                   │  → docs.attmosdiscovery.com/flowstack/
 *   ├────────────────────────────┤
 *   │  →  Sign out               │  (destructive-red)
 *   └────────────────────────────┘
 *
 * The trigger button itself is the circular initial avatar — a
 * one-letter fallback so the header does not need to fetch a Cognito
 * profile picture. Deferred: profile-picture upload / initials
 * consistency across the suite lives at the pool-attribute layer
 * (dispatch note in the M9.5 unify anchor); the identity source
 * here is the FlowStack session's ``display_name`` / ``email``.
 */
export function UserMenu({
  session,
  side,
  align = "end",
}: {
  session: Session;
  /** Popover placement. Defaults keep the original header behaviour. */
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}): JSX.Element {
  const logout = useLogout();

  const displayName = session.display_name?.trim() || session.email || "";
  // ``displayName`` already falls back to ``session.email`` above, so the
  // ``|| session.email`` a first-draft carried here was dead. Rupert
  // PR-611 r0 ADVISORY #1.
  const initial = _initialOf(displayName || "?");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Account menu for ${session.email || displayName}`}
          className="rounded-full"
        >
          <span
            aria-hidden
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full",
              "border border-border-subtle bg-surface-2",
              "text-sm font-medium text-foreground"
            )}
          >
            {initial}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="min-w-[240px]">
        {/* Identity header. Non-clickable label — kept separate from
            Settings so users can confirm which account they're in
            (matters with the shared attmos-users pool where the same
            browser may be authed against multiple ATTMOS apps). Same
            posture the pre-menu inline email text had. */}
        <DropdownMenuLabel className="flex flex-col gap-0.5 pb-2">
          <span className="text-sm font-medium text-foreground">
            {displayName || session.email}
          </span>
          {session.email && session.email !== displayName ? (
            <span
              className="max-w-[220px] truncate text-xs font-normal text-muted-foreground"
              title={session.email}
            >
              {session.email}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" aria-hidden />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/apps" className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" aria-hidden />
            <span>Apps</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          {/* External link — opens in new tab (matches orbit's Help
              behavior; the user stays in FlowStack while reading
              docs). ``rel=noopener,noreferrer`` closes the reverse-
              tabnabbing hole ``target=_blank`` opens by default. */}
          <a
            href="https://docs.attmosdiscovery.com/flowstack/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <HelpCircle className="h-4 w-4" aria-hidden />
            <span>Help</span>
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          // Rupert PR-611 r0 ADVISORY #2: guard against double-click
          // firing two POST /api/auth/logout requests. Idempotent
          // server-side, but the second call would race the cookie-
          // clear + queryClient-null teardown from ``useLogout``'s
          // ``onSettled`` and could surface a spurious 401 flash in
          // the SPA before RequireSession navigates away.
          disabled={logout.isPending}
          onSelect={() => logout.mutate()}
          className="flex items-center gap-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          <span>{logout.isPending ? "Signing out…" : "Sign out"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * First character of a display string, uppercased. Handles empty +
 * whitespace + emoji-ish inputs (grapheme-safe via ``Array.from``).
 * The trigger button always has an accessible name (``aria-label``),
 * so the visual initial is decorative — this only picks the letter
 * shown in the avatar, not identity semantics.
 */
function _initialOf(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return "?";
  const [first] = Array.from(trimmed);
  return (first ?? "?").toUpperCase();
}
